import ArrayHelper from '../../Core/helper/ArrayHelper.js';
import Widget from '../../Core/widget/Widget.js';

const { Chart, ChartDesigner } = globalThis.bryntum.charts ?? {};

/**
 * @module Grid/widget/GridChartDesigner
 */

/**
 * Subclass of {@link Chart.widget.ChartDesigner} that draws data series and values from the
 * selection of the configured {@link Grid.view.Grid}.
 *
 * {@inlineexample Grid/widget/GridChartDesigner.js}
 *
 * @extends Core/widget/Widget
 * @classtype gridchartdesigner
 * @demo Grid/charts
 * @widget
 */
export default class GridChartDesigner extends (ChartDesigner ?? Widget) {

    //region Config
    static $name = 'GridChartDesigner';

    // Factoryable type name
    static type = 'gridchartdesigner';

    static configurable = {
        /**
         * {@link Grid.view.Grid} whose selected cells will provide the data series for the chart.
         * Columns in the grid will become series available for use in the chart designer, and cell values
         * in the selected cells becomes data values shown in the preview chart.
         *
         * @config {Grid.view.Grid}
         */
        grid : null,

        /**
         * Whether to update the chart designer's data series options and preview data as the
         * grid selection changes.
         *
         * @config {Boolean}
         * @default
         */
        sync : false,

        items : {
            value : {
                settings : {
                    items : {
                        dataTab : {
                            items : {
                                sync : {
                                    type              : 'checkbox',
                                    text              : 'Sync with grid',
                                    internalListeners : {
                                        change : 'up.onSyncCheckboxChange'
                                    }
                                }
                            }
                        }
                    }
                }
            },
            $config : {
                merge : 'objects'
            }
        }
    };

    construct() {
        for (const className of ['Chart', 'ChartDesigner']) {
            if (!globalThis.bryntum.charts?.[className]) {
                throw new Error(`Missing class ${className}; make sure you've imported the Charts module.`);
            }
        }
        super.construct(...arguments);
    }

    changeItems(items) {
        return super.changeItems({
            ...items,
            preview : new Chart(items.preview)
        });
    }

    onSyncCheckboxChange({ value }) {
        this.sync = value;
    }

    updateSync(sync) {
        if (sync) {
            this.updateFromGridSelection();
        }
    }

    updateFromGridSelection() {
        const
            me = this,
            { grid } = me,
            model = grid.store.modelClass;

        let
            visibleColumnIndices,
            data;

        if (grid.selectedCells?.length > 0) {
            visibleColumnIndices = Array.from(new Set(grid.selectedCells.map(
                ({ column }) => grid.columns.visibleColumns.indexOf(column)))).sort();

            // Validate selection
            const
                cellsByRow = ArrayHelper.groupBy(grid.selectedCells, 'rowIndex'),
                allSameColumns = Object.entries(cellsByRow).every(([rowIndex, cells]) =>
                    cells.length === visibleColumnIndices.length);
            if (!allSameColumns) {
                // Unusable selection
                visibleColumnIndices = null;
            }
            else {
                data = Object.keys(cellsByRow)
                    .map(Number)
                    .sort((a, b) => a - b)
                    .map(rowIndex => cellsByRow[rowIndex][0].record);
            }
        }
        else if (grid.selectedRows) {
            // Take all visible columns
            visibleColumnIndices = grid.columns.visibleColumns.map((col, index) => index);
            data = grid.selectedRows;
        }

        if (!visibleColumnIndices || visibleColumnIndices.length === 0) {
            // Abort if empty selection
            me.labelsSeriesOptions = [];
            me.selectedLabelsSeriesId = null;
            me.series = [];
            me.data = [];
            return;
        }

        const
            { number = [], date = [], string = [] } = ArrayHelper.groupBy(
                visibleColumnIndices.map(columnIndex => grid.columns.visibleColumns[columnIndex]),
                column => model.fieldMap[column.field]?.type);

        me.series = number.map(this.columnToDataSeriesInfo);
        me.data = data;
        me.labelsSeriesOptions = [...string, ...date, ...number].map(this.columnToDataSeriesInfo);
    }

    columnToDataSeriesInfo(column) {
        return {
            field : column.field,
            label : column.text ?? column.name ?? column.field
        };
    }

    updateGrid(grid, oldGrid) {
        const name = 'selectionChange';
        oldGrid?.detachListeners(name);
        grid.ion({
            name,
            selectionChange : this.onGridSelectionChange,
            thisObj         : this
        });
        this.updateFromGridSelection();
    }

    onGridSelectionChange() {
        if (this.sync) {
            this.updateFromGridSelection();
        }
    }
}

GridChartDesigner.initClass?.();
